import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { fadeIn, fadeInUp } from "@/lib/motion-variants";
import {
  Clock,
  Coffee,
  AlertTriangle,
  X,
  Timer,
  Eye,
  Move,
} from "lucide-react";

export function ScreenTimeProvider({ children }) {
  const { user } = useAuth();
  const [screenTimeStatus, setScreenTimeStatus] = useState(null);
  const [showBreakReminder, setShowBreakReminder] = useState(false);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  
  // Fetch screen time status periodically
  const fetchStatus = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await axios.get(`${API}/safety/screen-time/status`, {
        withCredentials: true,
      });
      setScreenTimeStatus(response.data);
      
      // Check for break needed
      if (response.data.break_status?.break_needed) {
        setShowBreakReminder(true);
      }
      
      // Check for limit warning (< 10 minutes remaining)
      if (response.data.remaining_minutes !== null && response.data.remaining_minutes <= 10) {
        setShowLimitWarning(true);
      }
    } catch (error) {
      // Silently fail - safety features are optional
      console.debug("Screen time status fetch failed:", error);
    }
  }, [user]);
  
  // Start session on mount
  useEffect(() => {
    const startSession = async () => {
      if (!user) return;
      
      try {
        await axios.post(`${API}/safety/screen-time/start-session`, {}, {
          withCredentials: true,
        });
        setSessionActive(true);
      } catch (error) {
        // Session might already exist or feature disabled
        console.debug("Session start:", error.response?.data?.detail || error.message);
      }
    };
    
    startSession();
    fetchStatus();
    
    // Poll every minute
    const interval = setInterval(fetchStatus, 60000);
    
    // End session on unmount
    return () => {
      clearInterval(interval);
      if (sessionActive) {
        axios.post(`${API}/safety/screen-time/end-session`, {}, {
          withCredentials: true,
        }).catch(() => {});
      }
    };
  }, [user, fetchStatus, sessionActive]);
  
  const dismissBreakReminder = () => {
    setShowBreakReminder(false);
    // Re-show after break interval
    setTimeout(() => {
      fetchStatus();
    }, (screenTimeStatus?.break_status?.break_duration_minutes || 5) * 60 * 1000);
  };
  
  return (
    <>
      {children}
      
      {/* Screen Time Indicator (Bottom right) */}
      {screenTimeStatus && screenTimeStatus.remaining_minutes !== null && (
        <ScreenTimeIndicator status={screenTimeStatus} />
      )}
      
      {/* Break Reminder Modal */}
      <AnimatePresence>
        {showBreakReminder && (
          <BreakReminderModal
            breakDuration={screenTimeStatus?.break_status?.break_duration_minutes || 5}
            onDismiss={dismissBreakReminder}
          />
        )}
      </AnimatePresence>
      
      {/* Limit Warning Modal */}
      <AnimatePresence>
        {showLimitWarning && screenTimeStatus?.remaining_minutes <= 10 && (
          <LimitWarningModal
            remainingMinutes={screenTimeStatus.remaining_minutes}
            onDismiss={() => setShowLimitWarning(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
}

function ScreenTimeIndicator({ status }) {
  const percentage = status.daily_limit 
    ? Math.min(100, (status.used_today / status.daily_limit) * 100)
    : 0;
  
  const getColor = () => {
    if (percentage >= 90) return "text-red-500";
    if (percentage >= 70) return "text-amber-500";
    return "text-green-500";
  };
  
  return (
    <motion.div
      {...fadeInUp} 
      className="fixed bottom-4 right-4 z-40"
    >
      <div className="bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-slate-200 p-3 min-w-[180px]">
        <div className="flex items-center gap-2 mb-2">
          <Clock className={`w-4 h-4 ${getColor()}`} />
          <span className="text-sm font-medium text-slate-700">Screen Time</span>
        </div>
        <Progress value={percentage} className="h-2 mb-1" />
        <div className="flex justify-between text-xs text-slate-500">
          <span>{status.used_today || 0}m used</span>
          <span className={getColor()}>
            {status.remaining_minutes}m left
          </span>
        </div>
      </div>
    </motion.div>
  );
}

function BreakReminderModal({ breakDuration, onDismiss }) {
  const [countdown, setCountdown] = useState(breakDuration * 60);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          onDismiss();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [onDismiss]);
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };
  
  return (
    <motion.div
      {...fadeIn} 
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="break-reminder-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Break reminder"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl"
      >
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center">
          <Coffee className="w-10 h-10 text-white" />
        </div>
        
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          Time for a Break! 
        </h2>
        
        <p className="text-slate-600 mb-6">
          You've been learning for a while. Take a {breakDuration}-minute break to rest your eyes and stretch!
        </p>
        
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="p-3 bg-blue-50 rounded-xl">
            <Eye className="w-6 h-6 text-blue-500 mx-auto mb-1" />
            <p className="text-xs text-blue-700">Look away from screen</p>
          </div>
          <div className="p-3 bg-green-50 rounded-xl">
            <Move className="w-6 h-6 text-green-500 mx-auto mb-1" />
            <p className="text-xs text-green-700">Stretch your body</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-xl">
            <Timer className="w-6 h-6 text-purple-500 mx-auto mb-1" />
            <p className="text-xs text-purple-700">Walk around</p>
          </div>
        </div>
        
        <div className="text-4xl font-bold text-amber-600 mb-6">
          {formatTime(countdown)}
        </div>
        
        <Button
          onClick={onDismiss}
          variant="outline"
          className="w-full"
          data-testid="dismiss-break-btn"
        >
          I'll take a break now
        </Button>
      </motion.div>
    </motion.div>
  );
}

function LimitWarningModal({ remainingMinutes, onDismiss }) {
  return (
    <motion.div
      {...fadeIn} 
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="limit-warning-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Screen time limit warning"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="bg-white rounded-2xl p-8 max-w-md text-center shadow-2xl relative"
      >
        <button
          onClick={onDismiss}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
          data-testid="close-limit-warning"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-red-400 to-orange-500 rounded-full flex items-center justify-center">
          <AlertTriangle className="w-10 h-10 text-white" />
        </div>
        
        <h2 className="text-2xl font-bold text-slate-900 mb-2">
          Almost Out of Time!
        </h2>
        
        <p className="text-slate-600 mb-4">
          You have only <span className="font-bold text-red-600">{remainingMinutes} minutes</span> of screen time left today.
        </p>
        
        <p className="text-sm text-slate-500 mb-6">
          Make sure to save your work and finish up what you're doing!
        </p>
        
        <Button
          onClick={onDismiss}
          className="w-full bg-gradient-to-r from-violet-500 to-purple-600"
          data-testid="acknowledge-limit-btn"
        >
          Got it, I'll wrap up
        </Button>
      </motion.div>
    </motion.div>
  );
}

export default ScreenTimeProvider;
